import * as Api from '@ViewModels';
import { useCallback } from 'react';
import { useStepSave, type BaseStepOverrides } from './useStepSaveFactory';
import type { useAutoSave } from './useAutoSave';

interface UseEmailLikeStepSaveProps {
	step: Api.IEmailAutomationStep | Api.IHtmlNewsletterAutomationStep | null;
	stepId: string | undefined;
	stepType: Api.AutomationStepType;
	templateId: string | undefined;
	name: string;
	tagToAddOnClick: string;
	skipStepContactFilter: Api.IContactsFilterRequest | null | undefined;
	autoSave: ReturnType<typeof useAutoSave>;
}

export interface UseEmailLikeStepSaveOverrides extends BaseStepOverrides {
	newName?: string;
	newSendFrom?: Api.ISendFromOptions | null;
	newTagToAddOnClick?: string;
	newSendToSender?: boolean | null;
	newContactFilter?: Api.IContactsFilterRequest | null;
}

interface UseEmailLikeStepSaveResult {
	save: (overrides?: UseEmailLikeStepSaveOverrides) => Promise<void>;
}

/**
 * Shared save hook for Email and HtmlNewsletter step renderers.
 *
 * Both step types share the same save shape: name, tagToAddOnClick,
 * contactFilter, sendFrom, and sendToSender. The only difference is the
 * `stepType` passed in (Email vs HtmlNewsletter).
 */
export function useEmailLikeStepSave({
	step,
	stepId,
	stepType,
	templateId,
	name,
	tagToAddOnClick,
	skipStepContactFilter,
	autoSave,
}: UseEmailLikeStepSaveProps): UseEmailLikeStepSaveResult {
	const buildUpdatedStep = useCallback(
		(
			currentStep: Api.IEmailAutomationStep | Api.IHtmlNewsletterAutomationStep | null,
			overrides: UseEmailLikeStepSaveOverrides
		): Partial<Api.IEmailAutomationStep | Api.IHtmlNewsletterAutomationStep> => {
			const effectiveName = overrides.newName ?? name;
			const effectiveTagToAddOnClick = overrides.newTagToAddOnClick ?? tagToAddOnClick;
			const effectiveContactFilter =
				overrides.newContactFilter !== undefined ? overrides.newContactFilter : skipStepContactFilter;

			const updatedStep: Partial<Api.IEmailAutomationStep & Api.IHtmlNewsletterAutomationStep> = {
				...currentStep,
				name: effectiveName || currentStep?.name || '',
				tagToAddOnClick: effectiveTagToAddOnClick || '',
				contactFilter: effectiveContactFilter,
			};

			if (overrides.newSendFrom !== undefined) {
				updatedStep.sendFromOptions = overrides.newSendFrom;
			}

			if (overrides.newSendToSender !== undefined) {
				if (!updatedStep.options) {
					updatedStep.options = {
						noteVisibility: 'all',
						saveAsNote: true,
					};
				}
				updatedStep.options = {
					...updatedStep.options,
					sendToSender: !!overrides.newSendToSender,
				};
			}

			return updatedStep;
		},
		[name, tagToAddOnClick, skipStepContactFilter]
	);

	return useStepSave<Api.IEmailAutomationStep | Api.IHtmlNewsletterAutomationStep, UseEmailLikeStepSaveOverrides>({
		step,
		stepId,
		stepType,
		templateId,
		autoSave,
		buildUpdatedStep,
	});
}
